import React, { useEffect, useRef, useState, type ReactElement } from "react";
import { Textarea } from "@ndd/ui";
import type {
  RdScheduleActionRequestBody,
  RdScheduleActionScope,
} from "../../../../../shared/rd-schedule-actions";
import { localParts, weekdayName, whenLabel } from "../../../lib/rd-schedule-actions";
import {
  clearPendingNote,
  noteLockPlan,
  PENDING_NOTE_WINDOW_MS,
  pendingNoteStatus,
  setPendingNote,
  usePendingNote,
} from "../../../lib/rd-schedule-notes";
import type { ActionAppointment, ActionDialogContext } from "./action-types";
import { ActionDialogFrame, ActionForm, OptionsStatus } from "./action-frame";
import { Field, ScopeFields } from "./action-parts";
import { useActionOptions, type ActionOptions } from "./use-action-options";
import { useActionSubmit, useScheduleApi } from "./use-action-submit";

// NO-876. Edit note (spec § Appointment actions; canvas Edit.dc.html): the
// visit's note in Healthie, which only the RD sees, and which visits it covers.
// Nothing here reaches the Client: the note for the Client is stored in
// always_ai only and nothing sends it, so it is not edited here. The type and
// the time are not edited here either: a new time is Reschedule's job, and a
// different type is a cancel and rebook.

export interface EditDetailsDialogProps extends ActionDialogContext {
  appointment: ActionAppointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

export function EditDetailsDialog(props: EditDetailsDialogProps): ReactElement {
  const { appointment, timeZone, open, onOpenChange } = props;
  const [pending, setPending] = useState(false);

  return (
    <ActionDialogFrame
      open={open}
      onOpenChange={onOpenChange}
      pending={pending}
      title="Edit note"
      subtitle={`${appointment.clientName}, ${whenLabel(appointment.startsAt, timeZone)}`}
    >
      <EditBody {...props} onPending={setPending} />
    </ActionDialogFrame>
  );
}

function EditBody(
  props: EditDetailsDialogProps & { onPending: (pending: boolean) => void }
): ReactElement {
  const api = useScheduleApi();
  const { appointment, rdId } = props;
  // An earlier save the worker has not written to Healthie yet: the first read
  // can hold the old note and lock the form. While it does, the options are read
  // again every few seconds, so the lock lifts and the edit is built on the
  // note Healthie now has.
  const pending = usePendingNote(appointment.id);
  const { state, reload } = useActionOptions(
    api,
    rdId,
    appointment.id,
    true,
    pending !== null,
    PENDING_NOTE_WINDOW_MS
  );
  // The form stays up through any later read (a retry, the read after the window):
  // only the first one shows loading or an error.
  const lastReady = useRef<{ id: string; options: ActionOptions } | null>(null);

  if (state.status === "ready") {
    lastReady.current = { id: appointment.id, options: state.options };
  }

  const options =
    lastReady.current?.id === appointment.id ? lastReady.current.options : null;
  const plan = noteLockPlan(pending, options?.current.privateNote, Date.now());
  const [, wake] = useState(0);
  const locked = plan.settleInMs !== null;
  const settleInMs = plan.settleInMs ?? 0;

  // Nothing else renders when the window ends, so wake then and read once more.
  useEffect(() => {
    if (!locked) return undefined;

    const timer = setTimeout(() => {
      wake((count) => count + 1);
      reload();
    }, settleInMs);

    return () => clearTimeout(timer);
    // Armed once per pending save: `settleInMs` is the time left when it is armed.
  }, [locked, pending]);

  if (options === null) {
    return (
      <OptionsStatus
        state={state}
        onRetry={reload}
        onClose={() => props.onOpenChange(false)}
      />
    );
  }

  return <EditForm {...props} options={options} />;
}

export interface EditDraft {
  privateNote: string;
  scope: RdScheduleActionScope;
}

/** The note on file, empty when there is none. */
function noteOnFile(options: ActionOptions): string {
  return options.current.privateNote ?? "";
}

function initialDraft(options: ActionOptions, savingNote: string | null): EditDraft {
  return {
    privateNote: savingNote ?? noteOnFile(options),
    scope: "this",
  };
}

/** A series: the edit can be written over the later visits too. */
function repeatsLater(options: ActionOptions): boolean {
  return options.series !== null;
}

/** The write for a changed note, or null when nothing did (whitespace alone is not a change). */
export function buildEditBody(
  appointmentId: string,
  options: ActionOptions,
  draft: EditDraft
): RdScheduleActionRequestBody | null {
  const changed =
    options.current.privateNoteAvailable &&
    draft.privateNote.trim() !== (options.current.privateNote ?? "").trim();

  if (!changed) return null;

  return {
    kind: "edit",
    appointmentId,
    scope: repeatsLater(options) ? draft.scope : "this",
    // Nothing the RD can send reaches the Client from here, so no email.
    emailClient: false,
    privateNote: draft.privateNote,
    currentPrivateNote: options.current.privateNote,
  };
}

function EditForm(
  props: EditDetailsDialogProps & {
    options: ActionOptions;
    onPending: (pending: boolean) => void;
  }
): ReactElement {
  const { appointment, rdId, options, onChanged, onOpenChange, onPending } = props;
  const api = useScheduleApi();
  // A save the worker has not written to Healthie yet: the read can still hold
  // the old note, and an edit built on it would be refused as a conflict.
  const pendingSave = usePendingNote(appointment.id);
  const pendingStatus = pendingNoteStatus(
    pendingSave,
    options.current.privateNote,
    Date.now()
  );
  const saving = pendingStatus === "saving";
  const [draft, setDraft] = useState<EditDraft>(() =>
    initialDraft(options, saving && pendingSave ? pendingSave.note : null)
  );
  const action = useActionSubmit({
    api,
    rdId,
    timeZone: props.timeZone,
    onPartial: onChanged,
    onSucceeded: () => {
      // The panel that reopens shows this note until Healthie returns it.
      setPendingNote(appointment.id, draft.privateNote);
      onChanged();
      onOpenChange(false);
    },
  });
  const body = buildEditBody(appointment.id, options, draft);

  const change = (patch: Partial<EditDraft>) => {
    action.clearProblem();
    setDraft((current) => ({ ...current, ...patch }));
  };

  useEffect(() => onPending(action.pending), [onPending, action.pending]);

  // Healthie has the note (or the window passed): nothing is pending any more.
  useEffect(() => {
    if (pendingStatus === "settled") clearPendingNote(appointment.id);
  }, [pendingStatus, appointment.id]);

  return (
    <ActionForm
      onSubmit={() => body && !saving && void action.submit(body)}
      problem={action.problem}
      back={{ label: "Back", onClick: () => onOpenChange(false) }}
      confirm={{
        label: "Save note",
        ...action.confirmState,
        disabled: body === null || saving,
      }}
    >
      <EditFields
        {...{ appointment, options, draft, change }}
        timeZone={props.timeZone}
        saving={saving}
        disabled={action.pending || action.locked}
      />
    </ActionForm>
  );
}

interface EditFieldsProps {
  appointment: ActionAppointment;
  options: ActionOptions;
  draft: EditDraft;
  change: (patch: Partial<EditDraft>) => void;
  timeZone: string;
  disabled: boolean;
  /** An earlier save is still reaching Healthie: the note shows, and cannot change yet. */
  saving?: boolean;
}

export function EditFields(props: EditFieldsProps): ReactElement {
  const { appointment, options, draft, change, timeZone, disabled, saving } = props;
  const day = weekdayName(localParts(appointment.startsAt, timeZone).date);

  return (
    <>
      <NoteField
        appointment={appointment}
        options={options}
        draft={draft}
        change={change}
        disabled={disabled || saving === true}
      />
      {saving ? (
        <p role="status" className="text-body text-muted-foreground">
          Your last change is still saving to Healthie.
        </p>
      ) : null}
      {repeatsLater(options) ? (
        <ScopeFields
          idPrefix="edit-scope"
          legend="Apply to"
          value={draft.scope}
          onChange={(scope) => change({ scope })}
          disabled={disabled}
          thisLabel="Only this visit"
          laterLabel={`This and all later ${day} visits`}
          laterHint={`Replaces the note on every later ${day} visit in this series.`}
          laterDisabledNote={
            options.series?.tooLong
              ? "This series has too many later visits to change at once."
              : undefined
          }
        />
      ) : null}
    </>
  );
}

function NoteField(props: {
  appointment: ActionAppointment;
  options: ActionOptions;
  draft: EditDraft;
  change: (patch: Partial<EditDraft>) => void;
  disabled: boolean;
}): ReactElement {
  const { appointment, options, draft, change, disabled } = props;

  if (!options.current.privateNoteAvailable) {
    return (
      <p className="text-body text-muted-foreground">
        Healthie did not return the note, so it can't be edited right now.
      </p>
    );
  }

  return (
    <Field
      id="edit-note"
      label="Note"
      hint={`${appointment.firstName} doesn't see this.`}
    >
      <Textarea
        id="edit-note"
        rows={3}
        value={draft.privateNote}
        disabled={disabled}
        aria-describedby="edit-note-hint"
        onChange={(event) => change({ privateNote: event.target.value })}
      />
    </Field>
  );
}
